// 商品ページの在庫表示（在庫数・お届け予定日）と、在庫超過時のカート制御（Issue #31）。
// wuSiteResource2 の Shared/ProductDetails.ts にある checkStockRSP* と setupStockLimit() の引き取り先。
//
//   1. /api/stock-check の HTML 断片（.stockInfo）を、店ごとの差し込み先の直後に置く
//   2. 店によっては、注文枚数（枚数バリエーション × 数量）が data-stock を超えたら「カートに入れる」を
//      無効化して警告を出す（LIMIT_SITES）
//
// 発動する店は shared/hooks.ts の STOCK_CHECK_SITES で決める。★wuSiteResource2 側の呼び出し
// （各店 ProductDetails.ts の checkStockRSP* / setupStockLimit）を外すのと**同じ店を同時に**進める。
// 一時的に両方が走っても、どちらも「既存の .stockInfo を消してから差し込む」ので二重には残らない
// （storefront.js のキャッシュ 5 分のあいだだけ重なる）。
//
// 維持している挙動（現行どおり）:
//   - 予約購入ページ（#wu-reserve）では何もしない → .stockInfo が無いのでカート制御も掛からない
//   - API が空を返したら .stockInfo を消すだけ（在庫非表示・対象外の商品）
//   - data-stock が無い／空／数値でない → 数量制限しない（フェイルオープン）
//   - 枚数バリエーションのテキストから「N枚」を読む。読めなければ制限しない
//   - 超過時の文言（「ご注文数量（…）が在庫数（…）を超えています」）は出さない（2026-09-10 田口さん判断）
//
// 「在庫数以上をご希望の方はこちら →」ボタン（#zk-inq）もここで出す（INQUIRY の店だけ）。
// もとは ocnk 管理画面「マーケティング → SEO(メタタグ)全体設定 → ヘッダー」に直書きされていた
// <!-- fkro-item-prefill --> ブロック（ES さんの PoC。常時表示・カートボタンの下）を 2026-09-10 に引き取ったもの。
// 同日いったん「超過時だけ」に変えたが、2026-09-11 に「足りないお客様がそのまま帰ってしまう」として
// **常時表示・在庫表記（.stockInfo）の直後**に戻した。在庫切れ（data-stock=0）は文言を
// 「予約購入/入荷通知はこちら →」にする。おちゃのこ側で販売停止にした商品はカートのフォームが無いが、
// ボタンは .stockInfo に付くので同じく出る（そこから入荷通知・予約購入へ飛べる）。
//   - カート制御（applyStockLimit）は超過時に「カートに入れる」を無効化し、INQUIRY の店ではその直下にも
//     同じボタン（#zk-inq-over）を出す（2026-09-16 田口さん判断: 「数を変えたから出た → これを押せ」という
//     合図を復活させる。常時表示のほうは在庫表記の近くにあって、いま無効化されたボタンからは遠い）
//   - ★INQUIRY の店は在庫表記を**カートの下**に置く（fukuro / pack-japan はもともとそう。b-futou は 2026-09-30 に
//     差し込み先を返品特約の行からカートの行へ変えた。田口さん判断: 「カート → 在庫表記 → 案内ボタン」の順に揃える）。
//     万一テーマの変更で在庫表記がカートより上に来ても赤いボタン 2 つでカートを挟まないよう、DOM の前後関係を見て
//     常時表示ボタンをカート直下へ逃がす保険（placeInquiryBelowCart）を残してある
//   - 商品名（og:title か .goods_name）+ 品番（#h_m_number）を Cookie zk_last_item に 120 分保存するのは
//     そのまま（ES さんのフォーム #zk_item の先入れ用。hooks/stock-inquiry-prefill.ts）

const API = 'https://front.workup-system.com/api/stock-check'

/** 差し込み先（checkStockRSP* の after() のセレクタ）。表に無い店（dansai 4）は現行も呼んでいない */
const ANCHORS: Record<number, string> = {
  1: '.product_info_wrapper', // checkStockRSP3
  2: '.product_info_wrapper',
  3: '.product_info_wrapper',
  6: '.product_info_wrapper',
  9: '.detail_section.box_both_ends_space.return', // checkStockRSP / RSP2
  // b-futou は 2026-09-30 に返品特約の行（.return）からカートの行へ変更。在庫表記と案内ボタンを fukuro と同じくカートの下に出す
  10: '.detail_section.box_both_ends_space.add_cart',
  11: '.detail_section.box_both_ends_space.return',
  14: '.detail_section.box_both_ends_space.return',
  15: '.detail_section.box_both_ends_space.return',
  16: '.detail_section.box_both_ends_space.return',
}

/** カート制御を掛ける店。anshinya(2) は 2026-08-08 の決定で掛けない。他の店は現行も呼んでいない */
export const LIMIT_SITES = [1, 6, 10]

/**
 * 「表示以上の枚数〜」ボタンのリンク先（店ごと）。`{p}` は商品管理番号に置き換わる。
 * fukuro.in は 2026-09-10 に ES さんのフォーム(/page/2510)から wuStorefront の在庫不足ウィジェットの
 * ページ(/page/2510、?p= で商品を受ける。2522 はテスト用だったので同日 2510 に差し替え)へ変更。
 * pack-japan(6) は 2026-09-22 に足した（Issue #64。/page/694 を fukuro 方式にする。hooks/stock-shortage-page.ts と対。
 * それまではおちゃのこフッター欄の直書き pjp-item-prefill が /page/694（旧フォーム）へのボタンを出していた）。
 * b-futou(10) は 2026-09-30 に足した。旧フォームも直書きブロックも無かったので、/page/80 を新規に作り
 * （本文は fukuro と同じ <div data-wu-widget="stock-shortage" data-site="10"> の 1 行）、ここに行を足すだけ。
 * hooks/stock-shortage-page.ts の出番は無い
 */
export const INQUIRY: Record<number, string> = {
  1: '/page/2510?p={p}',
  6: '/page/694?p={p}',
  10: '/page/80?p={p}',
}

export const LAST_ITEM_COOKIE = 'zk_last_item'
export const INQUIRY_BUTTON_ID = 'zk-inq'
/** 超過時だけ「カートに入れる」の直下に出す 2 つ目のボタン */
export const INQUIRY_OVER_BUTTON_ID = 'zk-inq-over'
/** 常時表示ボタンをカート直下に置いたとき、超過時だけその上に出す文言 */
export const INQUIRY_NOTE_ID = 'zk-inq-note'

/** おちゃのこの商品ページ /product/{管理番号} から管理番号を取る。 */
export function productCodeFromPath(pathname: string): number | null {
  const m = /^\/product\/(\d+)\/?$/.exec(pathname)
  return m ? Number(m[1]) : null
}

/** data-stock の値 → 在庫数。無い／空／数値でなければ null（制限しない）。 */
export function parseDataStock(raw: string | null): number | null {
  if (raw == null || raw === '') return null
  const n = parseInt(raw.replace(/[^\d]/g, ''), 10)
  return isNaN(n) ? null : n
}

/**
 * 注文枚数。枚数バリエーションのテキスト（10 年安定の「N枚」形式）× 数量。
 * 読めない・数量が 1 未満なら null（制限を解除する）。
 */
export function orderTotal(variantText: string, quantityValue: string): number | null {
  const m = variantText.match(/([\d,]+)\s*枚/)
  if (m == null) return null
  const maisu = parseInt(m[1].replace(/,/g, ''), 10)
  const qty = parseInt(quantityValue, 10)
  if (isNaN(maisu) || isNaN(qty) || qty < 1) return null
  return maisu * qty
}

/** Cookie に入れる「商品名 / 品番」。og:title が無ければ .goods_name。名前が取れなければ null */
export function lastItemLabel(ogTitle: string | null, goodsName: string | null, modelNumber: string | null): string | null {
  const nm = (ogTitle || goodsName || '').replace(/\s+/g, ' ').trim()
  if (!nm) return null
  const kt = (modelNumber || '').replace(/[[\]［］]/g, '').trim()
  return nm + (kt ? ' / ' + kt : '')
}

function setCookie(name: string, value: string, minutes: number): void {
  const d = new Date(Date.now() + minutes * 60000)
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;expires=${d.toUTCString()};SameSite=Lax`
}

function rememberLastItem(): void {
  const og = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')
  const label = lastItemLabel(
    og?.content ?? null,
    document.querySelector('.goods_name')?.textContent ?? null,
    document.getElementById('h_m_number')?.textContent ?? null,
  )
  if (label) setCookie(LAST_ITEM_COOKIE, label, 120)
}

export function inquiryHref(template: string, productCode: number): string {
  return template.replace('{p}', String(productCode))
}

export const INQUIRY_LABEL_EXCEEDED = '在庫数以上をご希望の方はこちら →'
/** 超過時にカートボタンの下へ出す一言。「なぜカートに入らないか」と「どこを押すか」を同時に言う */
export const INQUIRY_OVER_NOTE = 'ご注文数量が在庫数を超えています。'
/** 在庫切れ用。在庫数との比較ではないので、飛び先（予約購入/入荷通知）を言う */
export const INQUIRY_LABEL_SOLD_OUT = '予約購入/入荷通知はこちら →'

/**
 * 常時表示のボタンをカートの直下に置くか（保険）。在庫表記（.stockInfo）が「カートに入れる」より**前**にある
 * ときだけ true。INQUIRY の 3 店はいずれもカートの後ろに差し込むので通常は false（従来の置き方）
 */
export function placeInquiryBelowCart(stockInfo: Element | null, cartBtn: Element | null): boolean {
  if (!stockInfo || !cartBtn) return false
  return (stockInfo.compareDocumentPosition(cartBtn) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
}

/**
 * 「在庫数以上〜」ボタンを .stockInfo の直後に置く（常時表示。2026-09-11 田口さん判断:
 * 足りないお客様がそのまま帰ってしまうのを避けるため、超過時だけでなく常に出し、在庫表記の近くに置く）。
 * 在庫切れ（stock<=0）は文言を「予約購入/入荷通知はこちら →」にする。
 * おちゃのこ側で販売停止にした商品はカートのフォームが無いが、このボタンはカートに依存しないので同じく出る
 */
export function ensureInquiryButton(stockInfo: Element, href: string, stock: number | null, after: Element = stockInfo): HTMLElement {
  let box = document.getElementById(INQUIRY_BUTTON_ID)
  if (box) return box
  box = document.createElement('div')
  box.id = INQUIRY_BUTTON_ID
  const belowCart = after !== stockInfo
  // カート直下に置くときは超過ボタン（#zk-inq-over）と同じ体裁。超過時の文言を先に持つ（既定は非表示）
  box.style.cssText = belowCart ? 'margin:8px 0' : 'margin:0 10px 12px;text-align:right'
  if (belowCart) {
    const note = document.createElement('p')
    note.id = INQUIRY_NOTE_ID
    note.style.cssText = 'margin:0 0 4px;color:#e60012;font-weight:bold;font-size:14px;display:none'
    note.textContent = INQUIRY_OVER_NOTE
    box.appendChild(note)
  }
  const soldOut = stock !== null && stock <= 0
  const a = document.createElement('a')
  // 在庫切れは ?soldout=1 を足す。案内ページ（widgets/stock-shortage）が「在庫表示分だけ納品」の選択肢を消して2択にする
  a.href = soldOut ? href + (href.includes('?') ? '&' : '?') + 'soldout=1' : href
  a.style.cssText =
    'display:inline-block;background:#e60012;color:#fff;text-decoration:none;font-weight:bold;font-size:15px;line-height:1.4;padding:6px 15px;border-radius:5px'
  a.textContent = soldOut ? INQUIRY_LABEL_SOLD_OUT : INQUIRY_LABEL_EXCEEDED
  box.appendChild(a)
  after.parentNode?.insertBefore(box, after.nextSibling)
  return box
}

/**
 * 超過時用のボタン（既定は非表示）。「カートに入れる」の直後に置く。
 * 常時表示の #zk-inq と同じ飛び先。数量を変えた瞬間にボタンの真下に出るので、
 * 「いま押したものが原因で、次はこれを押す」と伝わる
 */
export function ensureInquiryOverButton(cartBtn: Element, href: string): HTMLElement {
  let box = document.getElementById(INQUIRY_OVER_BUTTON_ID)
  if (box) return box
  box = document.createElement('div')
  box.id = INQUIRY_OVER_BUTTON_ID
  box.style.cssText = 'margin:8px 0;display:none'
  const note = document.createElement('p')
  note.style.cssText = 'margin:0 0 4px;color:#e60012;font-weight:bold;font-size:14px'
  note.textContent = INQUIRY_OVER_NOTE
  const a = document.createElement('a')
  a.href = href
  a.style.cssText =
    'display:inline-block;background:#e60012;color:#fff;text-decoration:none;font-weight:bold;font-size:17px;line-height:1.4;padding:6px 15px;border-radius:5px'
  a.textContent = INQUIRY_LABEL_EXCEEDED
  box.appendChild(note)
  box.appendChild(a)
  cartBtn.parentNode?.insertBefore(box, cartBtn.nextSibling)
  return box
}

/**
 * 在庫超過時のカート制御。現行 setupStockLimit() の移植（jQuery 無し）。
 * 超過したら「カートに入れる」を無効化する。inquiryHref を渡すと、その直下に 2 つ目のボタンを出す
 * （超過の間だけ。戻せば消える）。常時表示のボタン（#zk-inq）とは別
 */
export function applyStockLimit(
  root: ParentNode = document,
  inquiryHref: string | null = null,
  onExceeded: ((exceeded: boolean) => void) | null = null,
): void {
  const stockInfo = root.querySelector('.stockInfo')
  if (stockInfo == null) return
  const stock = parseDataStock(stockInfo.getAttribute('data-stock'))
  if (stock === null) return

  const variantSelect = root.querySelector<HTMLSelectElement>('select[id^="cart_addvariant_"]')
  const quantitySelect = root.querySelector<HTMLSelectElement>('select[id^="cart_addquantity_"]')
  if (variantSelect == null || quantitySelect == null) return

  const cartBtn = document.getElementById('submit_cart_input_btn') as HTMLButtonElement | null
  if (cartBtn == null) return

  const over = inquiryHref ? ensureInquiryOverButton(cartBtn, inquiryHref) : null

  const setDisabled = (disabled: boolean) => {
    cartBtn.disabled = disabled
    cartBtn.classList.toggle('stock-limit-disabled', disabled)
  }

  const evaluate = () => {
    const opt = variantSelect.options[variantSelect.selectedIndex]
    const total = orderTotal(opt ? opt.text : '', quantitySelect.value)
    const exceeded = total !== null && total > stock
    setDisabled(exceeded)
    if (over) over.style.display = exceeded ? '' : 'none'
    if (onExceeded) onExceeded(exceeded)
  }

  variantSelect.addEventListener('change', evaluate)
  quantitySelect.addEventListener('change', evaluate)
  evaluate()
}

export default async function stockCheck(): Promise<void> {
  if (document.getElementById('wu-reserve')) return // 予約購入ページでは在庫を出さない

  const siteId = window.wu?.siteInfoId
  const code = productCodeFromPath(location.pathname)
  if (!siteId || code === null) return
  const anchorSelector = ANCHORS[siteId]
  if (!anchorSelector) return

  if (INQUIRY[siteId]) rememberLastItem()

  let html = ''
  try {
    const r = await fetch(`${API}?productCode=${code}&siteInfoId=${siteId}`, { cache: 'no-store' })
    if (r.ok) html = await r.text()
  } catch (e) {
    console.warn('[wu] stock-check: 取得できません', e)
    return
  }

  document.querySelectorAll('.stockInfo').forEach((el) => el.remove())
  const anchor = document.querySelector(anchorSelector)
  if (!anchor || html === '') return
  anchor.insertAdjacentHTML('afterend', html)

  const inquiry = INQUIRY[siteId]
  const href = inquiry ? inquiryHref(inquiry, code) : null
  let belowCart = false
  if (href) {
    const stockInfo = document.querySelector('.stockInfo')
    const cartBtn = document.getElementById('submit_cart_input_btn')
    belowCart = placeInquiryBelowCart(stockInfo, cartBtn)
    if (stockInfo) {
      ensureInquiryButton(stockInfo, href, parseDataStock(stockInfo.getAttribute('data-stock')), belowCart ? cartBtn! : stockInfo)
    }
  }
  if (LIMIT_SITES.includes(siteId)) {
    // 常時表示ボタンがカート直下にあるときは 2 つ目のボタンを出さず、超過時の文言だけ切り替える
    const note = belowCart ? document.getElementById(INQUIRY_NOTE_ID) : null
    applyStockLimit(document, belowCart ? null : href, note ? (ex) => (note.style.display = ex ? '' : 'none') : null)
  }
}
